import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Eye, EyeOff } from 'lucide-react';
import { PasswordStrength, allCriteriaMet } from '@/components/PasswordStrength';

const monoFont = "'IBM Plex Mono', monospace";

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Listen for PASSWORD_RECOVERY event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setReady(true);
      }
    });
    // Also check hash for recovery token
    const hash = window.location.hash;
    if (hash.includes('type=recovery')) {
      setReady(true);
    }
    return () => subscription.unsubscribe();
  }, []);

  const valid = allCriteriaMet(password) && password === confirm;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setLoading(true);
    setError('');
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
    }
    setLoading(false);
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 14, fontWeight: 300,
    padding: '10px 12px', paddingRight: 40,
    backgroundColor: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.15)',
    color: 'rgba(255,255,255,0.85)', width: '100%', outline: 'none',
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#141414' }}>
        <div style={{ maxWidth: 320, textAlign: 'center' }}>
          <p style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: 'rgba(255,255,255,0.85)', marginBottom: 16 }}>
            Senha alterada com sucesso ✓
          </p>
          <p style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(255,255,255,0.5)' }}>
            Você já pode fechar esta janela e fazer login com sua nova senha.
          </p>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#141414' }}>
        <p style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: 'rgba(255,255,255,0.5)' }}>
          Verificando link de recuperação...
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#141414' }}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full max-w-xs">
        <h1 style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: 'rgba(255,255,255,0.85)', letterSpacing: '0.1em', marginBottom: 8 }}>
          REDEFINIR SENHA
        </h1>

        <div>
          <PasswordStrength password={password} theme="dark" />
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Nova senha"
              style={inputStyle}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: 0 }}>
              {showPassword ? <EyeOff size={16} strokeWidth={1} /> : <Eye size={16} strokeWidth={1} />}
            </button>
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <input
            type={showConfirm ? 'text' : 'password'}
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            placeholder="Confirmar nova senha"
            style={inputStyle}
          />
          <button type="button" onClick={() => setShowConfirm(!showConfirm)}
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: 0 }}>
            {showConfirm ? <EyeOff size={16} strokeWidth={1} /> : <Eye size={16} strokeWidth={1} />}
          </button>
        </div>

        {error && <p style={{ fontSize: 11, color: '#ff4444', fontFamily: monoFont }}>{error}</p>}

        <button type="submit" disabled={!valid || loading}
          style={{
            fontFamily: monoFont, fontSize: 13, fontWeight: 300, textTransform: 'uppercase',
            padding: '10px 16px', border: '1px solid rgba(255,255,255,0.15)',
            backgroundColor: 'transparent', color: valid ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.3)',
            cursor: valid && !loading ? 'pointer' : 'default', transition: 'all 0.15s ease',
            opacity: valid ? 1 : 0.5,
          }}>
          {loading ? 'SALVANDO...' : 'SALVAR NOVA SENHA'}
        </button>
      </form>
    </div>
  );
}
